import React, { ChangeEvent, useState, useContext, useEffect } from "react";
import { createStyles, makeStyles, Theme } from "@material-ui/core";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import Carousel from "react-material-ui-carousel";
import Box from "@material-ui/core/Box";
import TestRequestApi from "../../../api/services/TestRequestApi";
import useEnv from "../../../hooks/env";
import CustomDialog, { CustomDialogProps } from "../CustomDialog";
import CustomTextField from "../../Input/CustomTextField";
import { tesaBlue, tesaDarkRedError, tesaGrey1, tesaGrey5 } from "../../../styles/colors";
import LoadedFile from "../../Gallery/model/LoadedFile.model";
import ImageView from "../../Gallery/ImageView";
import GalleryButton from "../../Gallery/button/GalleryButton";
import FileHandler from "../../Gallery/model/FileHandler.model";
import ConfirmationDialogContext from "../ConfirmationDialog/ConfirmationDialogContext";
import Attachment from "../../Gallery/model/Attachment.model";
import NamingStrategy from "../../Gallery/model/NamingStrategy";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import { TestRequestContext } from "../../../contexts/TestRequestContext";
import { NavigationContext } from "../../../contexts/NavigationContext";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingLeft: "0px",
    },
    alignRight: {
      float: "right",
    },
    carouselViewPort: {
      height: "45vh",
      width: "60vh",
    },
    containerBottom: {
      display: "flex",
      flexDirection: "row",
      marginRight: theme.spacing(3),
      marginLeft: theme.spacing(3),
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(0),
      float: "right",
    },
    containerBottomRight: {
      display: "flex",
      justifyContent: "space-between",
    },
    description: {
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(3),
    },
    header: {
      marginBottom: "0px",
      marginLeft: theme.spacing(4),
      maxWidth: "50vh",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    headerContainer: {
      alignItems: "center",
      display: "flex",
    },
    imageContainer: {
      backgroundColor: tesaGrey5,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    marginRightS: {
      marginRight: theme.spacing(4),
    },
    minWidth: {
      minWidth: "50%",
    },
    navButtons: {
      // important overrides react-material-ui-carousel styles
      backgroundColor: tesaBlue,
      borderRadius: 0,
      height: theme.spacing(6),
      margin: 0,
      opacity: 1,
      width: theme.spacing(6),
      "&:active": {
        opacity: 0.5,
      },
    },
    navButtonsWrapper: {
      top: "-5px",
    },
    removeMarginTop: {
      marginTop: "0px",
    },
    subheader: {
      marginBottom: theme.spacing(2),
      marginLeft: theme.spacing(4),
      color: tesaGrey1,
      fontSize: "0.75rem",
      lineHeight: "0.75rem",
      whiteSpace: "nowrap",
      maxWidth: "55vh",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  }),
);

interface AttachmentDetailDialogProps extends CustomDialogProps {
  current?: LoadedFile;
  disabled?: boolean;
  fileHandler: FileHandler;
  images: LoadedFile[];
  namingStrategy: NamingStrategy;
  onDelete: (deletedFileId: string, updatedAttachments: Attachment[]) => void;
  onDescriptionChange: (id: string, newDescription: string) => void;
  showIndex?: boolean;
  testRequestId?: string; // Add testRequestId prop
}

const AttachmentDetailDialog: React.FC<AttachmentDetailDialogProps> = (props) => {
  const classes = useStyles(props);
  const {
    current,
    disabled,
    fileHandler,
    images,
    namingStrategy,
    onClose,
    onDelete,
    onDescriptionChange,
    open,
    showIndex,
    testRequestId,
  } = props;
  const { api } = useEnv();
  const testRequestApi = new TestRequestApi(api);
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { handleHttpError, handleDialogNegativeCallback } = useContext(ErrorSnackbarContext);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [imageFiles, setImageFiles] = useState<LoadedFile[]>([...images]);
  const [index, setIndex] = useState<number>(current ? images.map((file) => file.id).indexOf(current.id) : 0);
  const [attachmentDescription, setAttachmentDescription] = useState<string>(imageFiles[index]?.description || "");
  const [dirty, setDirty] = useState<boolean>(false);

  const findNextMatch = (previous: string[], next: string[], start: number): number => {
    for (let i = start; i < previous.length; i += 1) {
      const id = previous[i];
      const newIndex = next.indexOf(id);
      if (newIndex >= 0) {
        return newIndex;
      }
    }
    for (let i = 0; i < start; i += 1) {
      const id = previous[i];
      const newIndex = next.indexOf(id);
      if (newIndex >= 0) {
        return newIndex;
      }
    }
    return 0;
  };

  useEffect(() => {
    setAttachmentDescription(imageFiles[index]?.description || "");
  }, [index, imageFiles]);

  useEffect(() => {
    const description = imageFiles[index]?.description || "";
    setDirty(attachmentDescription.trim() !== description);
  }, [attachmentDescription]);

  useEffect(() => {
    const newIndex = current ? imageFiles.map((file) => file.id).indexOf(current.id) : 0;
    setIndex(newIndex);
  }, [current]);

  useEffect(() => {
    const newIndex = findNextMatch(
      imageFiles.map((imageFile) => imageFile.id),
      images.map((image) => image.id),
      index,
    );
    setIndex(newIndex);
    setImageFiles([...images]);
  }, [images]);

  const doDownload = (file: LoadedFile) => {
    setDownloading(true);
    fileHandler
      .downloadFile(file)
      .catch(handleHttpError)
      .finally(() => setDownloading(false));
  };

  const doDelete = (id: string) => {
    setDeleting(true);
    fileHandler
      .delete([id])
      .then((attachments) => onDelete(id, attachments))
      .catch(handleHttpError)
      .finally(() => setDeleting(false));
  };

  const doSave = (file: LoadedFile) => {
    const newDescription = attachmentDescription.trim();

    // Only proceed with description update if there are actual changes
    if (!dirty) {
      return;
    }
    setSaving(true);
    fileHandler
      .updateDescription(file, newDescription)
      .then(() => onDescriptionChange(file.id, newDescription))
      .catch(handleHttpError)
      .finally(() => setSaving(false));
  };

  const onChange = (i: number) => {
    if (i !== index) {
      doSave(imageFiles[index]);
      setIndex(i);
    }
  };

  const onSaveAndClose = () => {
    doSave(imageFiles[index]);
    onClose();
  };

  const onInternalClose = () => {
    if (dirty) {
      showConfirmationDialog({
        headline: "You have unsaved changes",
        text: "If you continue, your current changes to the file description will be discarded.",
        confirmText: "Save",
        additionalAction: {
          id: "DISCARD",
          text: "Discard",
          textColor: tesaDarkRedError,
        },
      })
        .then((action) => {
          if (action === "DEFAULT") {
            doSave(imageFiles[index]);
          }
          onClose();
        })
        .catch(handleDialogNegativeCallback);
    } else {
      onClose();
    }
  };

  const renderFile = (loadedFile: LoadedFile) => {
    if (loadedFile.isImage) {
      return (
        <ImageView
          id={loadedFile.id}
          downloadImage={(id) => fileHandler.downloadGallery(id)}
          onHttpError={handleHttpError}
        />
      );
    }
    return (
      <Box fontWeight={700} fontSize="36px" color={tesaGrey1}>
        {loadedFile.type.toUpperCase()}
      </Box>
    );
  };

  const confirmAction = (action: string) => {
    showConfirmationDialog({
      text: (
        <>
          You are about to delete <b>{imageFiles[index].name}</b>.
        </>
      ),
      confirmText: "Delete",
      confirmColor: tesaDarkRedError,
    }).then(() => {
      if (action === "DELETE") {
        doDelete(imageFiles[index].id);
      }
    });
  };

  const file = imageFiles[index];
  const headers = file ? namingStrategy.nameDetailDialog(file) : { header: "" };
  const indexField = `(${index + 1}/${imageFiles.length})`;

  return (
    <>
      <CustomDialog cutHorizPadding className={classes.root} onClose={onInternalClose} open={open} maxWidth="xl">
        <h2 className={classes.header}>{headers.header}</h2>
        <h4 className={[classes.subheader, classes.removeMarginTop].join(" ")}>
          {headers.subheader} {showIndex ? indexField : ""}
        </h4>

        <Box display="flex" flexDirection="row" justifyContent="center">
          {/* @ts-ignore */}
          <Carousel
            className={classes.carouselViewPort}
            navButtonsAlwaysInvisible={images.length <= 1}
            indicators={images.length > 1}
            index={index}
            onChange={onChange}
            autoPlay={false}
            NextIcon={<ArrowForwardIosIcon />}
            PrevIcon={<ArrowForwardIosIcon style={{ transform: "scaleX(-1)" }} />}
            // @ts-ignore
            navButtonsProps={{
              className: classes.navButtons,
            }}
            navButtonsWrapperProps={{
              // Move the buttons to the bottom. Unsetting top here to override default style.
              className: classes.navButtonsWrapper,
            }}
            activeIndicatorIconButtonProps={{
              style: { color: tesaBlue, opacity: 1 },
            }}
            indicatorIconButtonProps={{
              style: { color: tesaBlue, opacity: 0.5 },
            }}
          >
            {imageFiles.map((item) => (
              <div key={`carousel-slide-${item.id}`}>
                <div className={[classes.imageContainer, classes.carouselViewPort].join(" ")}>
                  {!deleting && renderFile(item)}
                </div>
              </div>
            ))}
          </Carousel>
        </Box>
        <Box marginLeft="1.5rem" marginRight="1.5rem">
          <CustomTextField
            autoFocus
            disabled={disabled || file?.editable === "READ_ONLY"}
            className={classes.description}
            fullWidth
            label="File Description"
            name="attachmentDescription"
            onChange={(e: ChangeEvent<HTMLInputElement>) => setAttachmentDescription(e.target.value)}
            value={attachmentDescription}
          />
        </Box>
        <div className={classes.containerBottom}>
          <Box marginRight={3}>
            <GalleryButton
              icon="DOWNLOAD"
              loading={downloading}
              onClick={() => {
                doDownload(imageFiles[index]);
              }}
              text="Download"
            />
          </Box>
          {!disabled && (
            <Box marginRight={3}>
              <GalleryButton
                icon="DELETE"
                loading={deleting}
                onClick={() => {
                  confirmAction("DELETE");
                }}
                text="Delete"
              />
            </Box>
          )}
          <GalleryButton loading={saving} onClick={onSaveAndClose} text={dirty ? "Save & Close" : "Close"} />
        </div>
      </CustomDialog>
    </>
  );
};

export default AttachmentDetailDialog;
